'use client';

import { useEffect, useState } from 'react';
import PriceDetails from '@/components/global/PriceDetails';
import { usePricing } from '@/contexts/PricingContext';
import { formatPrice } from '@/utils/utilities.mjs';
import CartCheckoutButton from './CartCheckoutButton';
import SecureCheckoutBadges from './SecureCheckoutBadges';
import InfoIcon from '@/components/icons/InfoIcon';

const CartOrderTotals = ({ cart }) => {
  const [showTaxTooltip, setShowTaxTooltip] = useState(false);
  const pricing = usePricing();
  const savings = pricing.cart.savings(cart);
  const subtotal = pricing.cart.subtotal(cart);
  const total = pricing.cart.total(cart);

  useEffect(() => {
    if (!showTaxTooltip) return undefined;

    const close = () => setShowTaxTooltip(false);
    const events = ['click', 'touchstart'];
    events.forEach((event) => document.addEventListener(event, close));
    return () => events.forEach((event) => document.removeEventListener(event, close));
  }, [showTaxTooltip]);

  return (
    <section className="w-full lg:w-100 flex flex-col gap-3 md:gap-6">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <h2 className="font-worksans text-2xl text-primary font-semibold">Order Summary</h2>
          <PriceDetails />
        </div>

        <dl className="border-y-2 border-primary py-6 space-y-2">
          <div className="flex w-full justify-between">
            <dt className="font-worksans text-primary">Subtotal ({cart?.totalQuantity})</dt>
            <dd>
              <span className="font-worksans text-primary font-semibold">
                {formatPrice(subtotal)}
              </span>
            </dd>
          </div>

          {savings > 0 && (
            <div className="summary-item flex w-full justify-between">
              <dt className="font-worksans text-primary">Total Savings</dt>
              <dd className="font-worksans text-primary font-semibold">{formatPrice(-savings)}</dd>
            </div>
          )}

          <div className="summary-item flex w-full justify-between">
            <dt className="font-worksans text-primary">Shipping</dt>
            <dd className="price-r font-worksans text-primary text-right">
              <span className="font-semibold">FREE for US orders</span>
              <p data-in-home-note="1">In-Home Setup &amp; Removal<br />Available at Checkout</p>
            </dd>
          </div>

          <div className="summary-item flex w-full justify-between">
            <dt className="font-worksans text-primary flex items-center gap-2">
              Estimated Taxes

              <div className="relative group">
                <button
                  type="button"
                  className="flex items-center cursor-pointer"
                  aria-label="More information about estimated taxes"
                  aria-expanded={showTaxTooltip}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTaxTooltip((prev) => !prev);
                  }}
                >
                  <InfoIcon outlineAsPrimary={false} className="size-4 text-primary" />
                </button>

                <div
                  role="tooltip"
                  className={`absolute bottom-6 -left-12 z-30 w-56 p-2 bg-white text-black rounded-lg shadow-lg ${showTaxTooltip ? '' : 'invisible group-hover:visible'}`}
                >
                  The final tax and total will be confirmed by email or text after you place your order.
                </div>
              </div>
            </dt>
            <dd className="body-s font-worksans text-primary italic">
              Calculated at checkout
            </dd>
          </div>

          <div className="summary-item flex w-full justify-between">
            <dt className="font-worksans text-primary">Promo Code</dt>
            <dd className="body-s font-worksans text-primary italic">Apply at checkout</dd>
          </div>
        </dl>
      </div>

      <div className="hidden lg:flex w-full max-lg:px-4 flex-col gap-3 md:gap-6">
        <dl className="summary-item sh10 order-total-section flex w-full justify-between">
          <dt className="font-worksans text-primary font-semibold">Total Before Tax</dt>
          <dd aria-live="polite" aria-atomic="true" className="font-worksans text-primary font-semibold">
            {formatPrice(total)}
          </dd>
        </dl>

        {/* Wrapped so the parent's md:gap-6 doesn't apply between the button and
            the badges — production keeps this pair 16px apart. */}
        <div className="flex flex-col gap-4">
          <CartCheckoutButton />
          <SecureCheckoutBadges />
        </div>
      </div>
    </section>
  );
};

export default CartOrderTotals;
